import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavigationContext } from '../../NavigationContext';
import { tabsPath } from '../components';
import { useHeaderState } from './useHeaderState';
import { useMenuState } from './useMenuState';
import { useTabsState } from './useTabsState';
export function useGlobalAppState(props, ref) {
    const navigationContext = React.useContext(NavigationContext);
    const location = useLocation();
    const navigate = useNavigate();
    const headerState = useHeaderState(navigationContext, navigate, location, props.basename, props.headerItems, props.headerPrimaryItem);
    const menuState = useMenuState(navigationContext, navigate, location, props.routes, props.basename, props.bottomMenuRoutes);
    const tabsState = useTabsState(navigationContext, navigate, location, props.tabs, props.onCloseTab, props.onCloseAllTabs, props.additionalTabBarActions);
    const [contextProfileBadge, setContextProfileBadge] = React.useState();
    const [contextInfoProps, setContextInfoProps] = React.useState();
    const [drawerOpen, setDrawerOpen] = React.useState(window.sessionStorage.getItem('commonWebUi.globalDrawerSlideoutOpen') === 'true');
    const { onCloseTab } = props;
    const { setContextMenuItemBadges } = menuState;
    const { selectedTabPath, tabs, setTabs } = tabsState;
    React.useImperativeHandle(ref, () => {
        return {
            setProfileBadge: setContextProfileBadge,
            setMenuItemBadge: (path, badge) => {
                setContextMenuItemBadges((prevState) => (Object.assign(Object.assign({}, prevState), { [path]: badge })));
            },
            setInfoProps: setContextInfoProps,
            getTabs: () => {
                return tabs !== null && tabs !== void 0 ? tabs : [];
            },
            setTabs: (tabs) => {
                setTabs(tabs);
            },
            openExistingTab: (existingTabPath) => {
                navigationContext.navigate(() => {
                    setTabs((prevState) => {
                        if (selectedTabPath !== existingTabPath) {
                            const prevTabs = prevState !== null && prevState !== void 0 ? prevState : [];
                            const existingTab = prevTabs.find((tab) => tab.path === existingTabPath);
                            if (existingTab) {
                                navigate(`${tabsPath}/${existingTab.path}`);
                            }
                        }
                        return prevState;
                    });
                });
            },
            openTab: (newOrExistingTab) => {
                navigationContext.navigate(() => {
                    setTabs((prevState) => {
                        const prevTabs = prevState !== null && prevState !== void 0 ? prevState : [];
                        const existingTabIndex = prevTabs.findIndex((tab) => tab.path === newOrExistingTab.path);
                        navigate(`${tabsPath}/${newOrExistingTab.path}`);
                        return existingTabIndex === -1
                            ? [...prevTabs, newOrExistingTab]
                            : prevTabs.map((tab, index) => (index === existingTabIndex ? newOrExistingTab : tab));
                    });
                });
            },
            closeTab: (tabToClosePath) => {
                navigationContext.navigate(() => {
                    setTabs((prevState) => {
                        var _a, _b, _c;
                        const prevTabs = prevState !== null && prevState !== void 0 ? prevState : [];
                        const tabToCloseIndex = prevTabs.findIndex((tab) => tab.path === tabToClosePath);
                        const closedTab = prevTabs[tabToCloseIndex];
                        if (tabToCloseIndex !== -1) {
                            if (selectedTabPath === tabToClosePath) {
                                const nextSelectedTab = (_a = prevTabs[tabToCloseIndex + 1]) !== null && _a !== void 0 ? _a : prevTabs[tabToCloseIndex - 1];
                                if (nextSelectedTab) {
                                    navigate(`${tabsPath}/${nextSelectedTab.path}`);
                                }
                                else {
                                    navigate('/');
                                }
                                onCloseTab === null || onCloseTab === void 0 ? void 0 : onCloseTab(closedTab, nextSelectedTab);
                                (_b = closedTab.onClose) === null || _b === void 0 ? void 0 : _b.call(closedTab, nextSelectedTab);
                            }
                            else {
                                onCloseTab === null || onCloseTab === void 0 ? void 0 : onCloseTab(closedTab);
                                (_c = closedTab.onClose) === null || _c === void 0 ? void 0 : _c.call(closedTab);
                            }
                            return prevTabs.filter((tab, index) => index !== tabToCloseIndex);
                        }
                        else {
                            return prevState;
                        }
                    });
                });
            },
            setTabBadge: (tabPath, badge) => {
                setTabs((prevState) => {
                    if (prevState) {
                        const tabToChangeIndex = prevState.findIndex((tab) => tab.path === tabPath && tab.badge !== badge);
                        if (tabToChangeIndex !== -1) {
                            const newTabs = [...(prevState !== null && prevState !== void 0 ? prevState : [])];
                            newTabs[tabToChangeIndex] = Object.assign(Object.assign({}, prevState[tabToChangeIndex]), { badge: badge });
                            return newTabs;
                        }
                    }
                    return prevState;
                });
            },
            setDrawerOpen: (open) => {
                setDrawerOpen(open);
            },
            toggleDrawerOpen: () => {
                setDrawerOpen((open) => !open);
            },
        };
    }, [navigationContext, navigate, onCloseTab, setContextMenuItemBadges, selectedTabPath, tabs, setTabs]);
    return {
        headerState,
        menuState,
        tabsState,
        contextProfileBadge,
        contextInfoProps,
        drawerOpen,
        setDrawerOpen,
    };
}
